/**
 * Internationalization issue model (Sprint-017.2 FR-7 and "Error Handling").
 *
 * Localization failures are *reported*, not thrown: a missing or malformed
 * translation must never crash the application (FR-6), and a broken plugin
 * language pack must never stop the UI from rendering. Every issue therefore
 * flows through a single reporting channel and carries the context the sprint
 * requires — locale, module, provider and translation key.
 *
 * This channel is also the feed the Sprint-017.6 localization tooling needs
 * (missing translation detector, duplicate key detection, validation report).
 */
/** The failure kinds the framework detects (Sprint-017.2 "Error Handling"). */
export const I18N_ISSUE_CODES = {
    /** A requested locale has no matching language, so a fallback was used. */
    UnsupportedLocale: 'unsupported-locale',
    /** A key is absent from the active language (and possibly from the fallback). */
    MissingTranslation: 'missing-translation',
    /** Two modules define the same translation key. */
    DuplicateKey: 'duplicate-key',
    /** A translation resource does not match the expected schema. */
    InvalidTranslationResource: 'invalid-translation-resource',
    /** A message is not valid ICU MessageFormat. */
    InvalidIcuMessage: 'invalid-icu-message',
    /** A message references a parameter the caller did not supply. */
    MissingParameter: 'missing-parameter'
};
export function createI18nIssue(code, message, context = {}) {
    return { code, message, ...context };
}
const ISSUE_TITLES = {
    [I18N_ISSUE_CODES.UnsupportedLocale]: 'Unsupported locale',
    [I18N_ISSUE_CODES.MissingTranslation]: 'Missing translation',
    [I18N_ISSUE_CODES.DuplicateKey]: 'Duplicate translation key',
    [I18N_ISSUE_CODES.InvalidTranslationResource]: 'Invalid translation resource',
    [I18N_ISSUE_CODES.InvalidIcuMessage]: 'Invalid ICU message',
    [I18N_ISSUE_CODES.MissingParameter]: 'Missing translation parameter'
};
/**
 * Renders an issue as the multi-line report described by FR-7:
 *
 * ```text
 * Missing translation
 *
 * Language : fr
 * Module   : floorplan
 * Key      : wall.properties.height
 * ```
 *
 * Formatting lives here rather than in a logger so that the package owns no
 * logging transport (ADR-0006) while hosts still log a consistent shape.
 */
export function formatI18nIssue(issue) {
    const rows = [
        ['Language', issue.language],
        ['Locale', issue.locale],
        ['Module', issue.module],
        ['Provider', issue.provider],
        ['Resource', issue.resourceId],
        ['Key', issue.key]
    ];
    const present = rows.filter((row) => row[1] !== undefined);
    const width = present.reduce((max, [label]) => Math.max(max, label.length), 0);
    const details = present.map(([label, value]) => `${label.padEnd(width)} : ${value}`);
    return [
        ISSUE_TITLES[issue.code],
        '',
        issue.message,
        ...(details.length > 0 ? [''] : []),
        ...details
    ].join('\n');
}
/**
 * Collects reported issues in memory.
 *
 * Useful for tests and for the localization tooling planned in Sprint-017.6,
 * which needs the whole set rather than a stream.
 */
export function createI18nIssueCollector() {
    const issues = [];
    return {
        report: (issue) => issues.push(issue),
        get issues() {
            return [...issues];
        },
        clear: () => {
            issues.length = 0;
        }
    };
}
//# sourceMappingURL=i18n-issues.js.map